import React, { useContext } from 'react';
import { FormikProps } from 'formik';
import { Route, RouterProvider } from '../../gs-component-right-rail';

import { ModalType, GuestModalProps, TabViews, FormProps, BulkFormProps, FocusOn } from '../interfaces';
import ComponentStateContext from '../ComponentStateContext';
import { doesAddressOnlyIncludeFullAddress } from '../helpers';
import DeleteConfirmation from '../DeleteConfirmation';
import CancelConfirmation from '../CancelConfirmation';
import SwitchConfirmation from '../CancelConfirmation/SwitchTab';
import RightRailContent from '../RightRailContent';
import RightRailBulkEdit from '../BulkEditGuests';
import GuestResponses from '../GuestResponses';
import HeaderWithTabs, { RESPONSES_TAB, GUEST_TAB as MAIN_VIEW } from '../RightRailContent/HeaderWithTabs';
import EditAnswers from '../EditAnswers';

const setDefaultView = (defaultView: string | undefined): TabViews => {
  if (!defaultView) return MAIN_VIEW;
  switch (defaultView) {
    case MAIN_VIEW:
    case RESPONSES_TAB:
      return defaultView;
    default:
      console.error(`Unsupported view type ${defaultView}`);
      return MAIN_VIEW;
  }
};

interface RoutesProps extends GuestModalProps {
  setShouldClose: React.Dispatch<React.SetStateAction<boolean>>;
  formProps: FormikProps<FormProps | BulkFormProps>;
}

const Routes: React.FC<RoutesProps> = ({
  guestWeddingQuestions,
  household,
  householdsList,
  events,
  wedding,
  selectedEvent,
  onClose,
  onDelete,
  analyticsObject,
  showError,
  defaultView,
  setShouldClose,
  formProps,
  focusOn,
  topLabel,
}) => {
  const { setGuestsDeleted } = useContext(ComponentStateContext);
  const type = household ? ModalType.edit : ModalType.add;
  const handleClose = (): void => {
    onClose();
    setGuestsDeleted(0);
  };
  const [currentView, setView] = React.useState<TabViews>(setDefaultView(defaultView));
  const leader = household?.people?.find(({ isLeader }) => isLeader) || undefined;
  const count =
    household?.people?.length && household.people.length > 1 ? `+ ${household.people.length - 1}` : undefined;
  const leaderAndCount = leader ? [leader.firstName, leader.lastName, count].join(' ') : undefined;

  const setResponseView = (): void => setView(RESPONSES_TAB);

  // Determine whether to display the FullAddress field of the Address on the Household,
  // until the user decides to edit it.
  // FullAddress is a legacy field that should be used for display only,
  // and only if none of the other properties exist.
  const displayFullAddress = !!(household && doesAddressOnlyIncludeFullAddress(household.address));

  // props for bulk update
  const contentPropsBulkEdit = {
    householdsList,
    onClose: handleClose,
    topLabel,
    setShouldClose,
    displayFullAddress,
    focusOn,
    isValid: formProps.isValid,
    submitForm: formProps.handleSubmit,
  };

  const contentProps = {
    // Filter out Old Template Reception event
    // We have no use for it
    // Guests can only be invited to the Ceremony event
    events: events.filter(e => e.type !== 'Reception'),
    household,
    householdsList,
    selectedEvent,
    onClose: handleClose,
    type,
    topLabel,
    setShouldClose,
    displayFullAddress,
    focusOn: focusOn ? focusOn : FocusOn.all,
  };

  return (
    <RouterProvider entry={currentView}>
      <Route
        screen="main"
        component={
          <>
            <HeaderWithTabs
              onClose={onClose}
              showTabs={type !== ModalType.add && focusOn === FocusOn.all}
              showConfirmation={formProps.dirty}
              currentView={currentView}
              setView={setView}
              topLabel={topLabel ? topLabel : type !== ModalType.add ? leaderAndCount : 'Add Party'}
            />
            {householdsList?.length && focusOn ? (
              <RightRailBulkEdit
                {...contentPropsBulkEdit}
                // @ts-ignore
                formProps={formProps as FormikProps<BulkFormProps>}
              />
            ) : (
              <RightRailContent {...contentProps} {...(formProps as FormikProps<FormProps>)} />
            )}
          </>
        }
      />
      <Route
        screen="responses"
        component={
          <>
            <HeaderWithTabs
              onClose={onClose}
              showTabs
              showConfirmation={false}
              currentView={currentView}
              setView={setView}
              topLabel={leaderAndCount}
            />
            <GuestResponses
              events={contentProps.events}
              household={formProps.values as FormProps}
              generalQuestions={guestWeddingQuestions}
            />
          </>
        }
      />
      <Route
        screen="cancel"
        component={
          <CancelConfirmation onClose={handleClose} setShouldClose={setShouldClose} onSubmit={formProps.handleSubmit} />
        }
      />
      <Route
        screen="switch"
        component={
          <SwitchConfirmation
            onSubmit={(e): void => {
              formProps.handleSubmit(e);
              setResponseView();
            }}
            onSwitch={(): void => {
              formProps.resetForm();
              setResponseView();
            }}
          />
        }
      />
      {household && (
        <Route
          screen="delete"
          component={
            <DeleteConfirmation
              onClose={handleClose}
              onDelete={onDelete}
              showError={showError}
              household={household}
              wedding={wedding}
              analyticsObject={analyticsObject}
            />
          }
        />
      )}
      <Route
        screen="edit_response"
        component={
          <EditAnswers
            onClose={onClose}
            formikValues={formProps.values}
            resetForm={formProps.resetForm}
            onSubmit={formProps.handleSubmit}
            events={events}
            guestWeddingQuestions={guestWeddingQuestions}
          />
        }
      />
    </RouterProvider>
  );
};

export default Routes;
